import { Button, CircularProgress, Typography, Accordion, AccordionSummary, AccordionDetails, Box, Switch, FormControlLabel } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import { getDataWithParams } from 'src/consumer/services/profileService'
import {
  NextVisitSlotSelectorButton,
  NextVisitSlotSelectorGridContainer,
} from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/styledComponents'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { formatTimeSlotInClinicTimezone, getClinicTimezone } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/utils/nextVisitRosterCalendarUtils'


// TimeSlotGrid Component
export const TimeSlotGrid = ({
  minDate,
  maxDate,
  selectedDate,
  setSelectedDate,
  onSlotSelect,
  eventDuration = 30,
  mentorProfile,
  currentBookedEventId,
  currentEventTypeObject,
  instructionsDiv,
  clinicLocation = null, // Clinic location for timezone
  bookedSlots = [], // Booked slots to disable overlapping slots
  bookedSlotsLoading = false, // Loading state for booked slots
}) => {
  const mentorId = useMemo(() => mentorProfile?.id, [mentorProfile?.id])

  const [availableSlots, setAvailableSlots] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [nightSlotsExpanded, setNightSlotsExpanded] = useState(false) // Night slots collapsed by default
  const [earlyMorningSlotsExpanded, setEarlyMorningSlotsExpanded] = useState(false) // Early morning slots collapsed by default
  const [showBlockedSlots, setShowBlockedSlots] = useState(false) // Hide blocked slots by default

  // Fetch available slots from API
  const fetchAvailableSlots = async () => {
    if (!mentorProfile?.id || !selectedDate || !currentEventTypeObject) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Get booking function based on mentor ID

      // Set start and end time for the selected date in clinic timezone
      // Get clinic timezone (fallback to +05:30 IST)
      const clinicTimezone = getClinicTimezone(clinicLocation)
      
      // Convert selectedDate to dayjs if needed
      const selectedDateDayjs = dayjs.isDayjs(selectedDate) ? selectedDate : dayjs(selectedDate)
      
      // Parse the selected date string (format: DD-MM-YYYY or YYYY-MM-DD)
      const selectedDateStr = selectedDateDayjs.format('YYYY-MM-DD')
      
      // Create start of day (12 midnight) in clinic timezone, then convert to UTC
      // Parse offset string (e.g., '+05:30') to get hours and minutes
      const offsetMatch = clinicTimezone.match(/^([+-])(\d{2}):(\d{2})$/)
      let startTime, endTime
      
      if (offsetMatch) {
        const sign = offsetMatch[1] === '+' ? 1 : -1
        const hours = parseInt(offsetMatch[2], 10)
        const minutes = parseInt(offsetMatch[3], 10)
        const totalMinutes = sign * (hours * 60 + minutes)
        
        // To get midnight in clinic timezone converted to UTC:
        // If clinic is +05:30 ahead of UTC, then midnight in clinic = 18:30 previous day in UTC
        // So we create midnight UTC on the selected date, then subtract the offset
        // Example: For 2024-01-15 00:00:00 in +05:30:
        //   UTC = 2024-01-15 00:00:00 - 05:30 = 2024-01-14 18:30:00 UTC
        const startOfDayUTC = dayjs.utc(`${selectedDateStr}T00:00:00`)
        const startTimeUTC = startOfDayUTC.subtract(totalMinutes, 'minute')
        
        const endOfDayUTC = dayjs.utc(`${selectedDateStr}T23:59:59`)
        const endTimeUTC = endOfDayUTC.subtract(totalMinutes, 'minute')
        
        startTime = startTimeUTC.toISOString()
        endTime = endTimeUTC.toISOString()
      } else {
        // Fallback: use local timezone (shouldn't happen as getClinicTimezone always returns a value)
        const startOfDayLocal = selectedDateDayjs.startOf('day')
        const endOfDayLocal = selectedDateDayjs.endOf('day')
        startTime = startOfDayLocal.utc().toISOString()
        endTime = endOfDayLocal.utc().toISOString()
      }
      const zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'

      const response = await getDataWithParams(
        // `/api/secure/mentor/available-slots?mentorId=${
        //   mentorProfile.id
        // }&startTime=${startTime}&endTime=${endTime}&eventType=${
        //   currentEventTypeObject.uid || currentEventTypeObject.id
        // }&zoneId=${zoneId}`,
        `/api/secure/mentor/available-slots`,
        {
          mentorId: mentorProfile.id,
          startTime: startTime,
          endTime: endTime,
          eventTypeId: currentEventTypeObject.uid || currentEventTypeObject.id,
          eventType: currentEventTypeObject.type,
          zoneId: zoneId,
        },
      )

      if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const slotData = getDataMapFromCommonResponse(response)?.slots || []
        setAvailableSlots(slotData)
      } else {
        setAvailableSlots([])
      }
    } catch (error) {
      console.error('Error fetching available slots:', error)
      setError('Failed to load available slots')
      setAvailableSlots([])
    }

    setLoading(false)
  }

  // Fetch slots when component mounts or dependencies change
  useEffect(() => {
    fetchAvailableSlots()
  }, [mentorId, selectedDate, currentEventTypeObject])

  // Organize slots by time periods
  const organizeSlotsByTimePeriod = () => {
    const periods = {
      earlyMorning: { label: '🌄 Early Morning', slots: [] },
      morning: { label: '🌅 Morning', slots: [] },
      afternoon: { label: '☀️ Afternoon', slots: [] },
      evening: { label: '🌆 Evening', slots: [] },
      night: { label: '🌙 Night', slots: [] },
    }

    // Get selected date string for filtering (in clinic timezone)
    const clinicTimezone = getClinicTimezone(clinicLocation)
    const selectedDateDayjs = selectedDate ? (dayjs.isDayjs(selectedDate) ? selectedDate : dayjs(selectedDate)) : null
    const selectedDateStr = selectedDateDayjs ? selectedDateDayjs.format('YYYY-MM-DD') : null

    availableSlots
      ?.filter((slot) => {
        // Slots from API are typically UTC ISO strings
        const slotDateTime = dayjs.utc(slot)
        
        // Filter out next day slots - only show slots for the selected date in clinic timezone
        if (selectedDateStr) {
          // Convert slot to clinic timezone to check the date
          const formattedTime = formatTimeSlotInClinicTimezone(
            slotDateTime,
            null,
            clinicLocation,
            eventDuration
          )
          const slotDateInTz = formattedTime.startInTz.format('YYYY-MM-DD')
          
          // Only include slots that are on the selected date (in clinic timezone)
          // This ensures we include all slots from 12 midnight onwards in clinic timezone
          if (slotDateInTz !== selectedDateStr) {
            return false
          }
          
          // If slot is on the selected date, include it regardless of minDate time
          // This ensures early morning slots (12 midnight to 6am) are shown
          return true
        }
        
        // If no selectedDate, fall back to minDate filtering
        if (minDate) {
          const minDateDayjs = dayjs.isDayjs(minDate) ? minDate : dayjs(minDate)
          // For minDate, we check if slot is after minDate (allows same day slots)
          return slotDateTime.isSameOrAfter(minDateDayjs.startOf('day'))
        }
        
        return true
      })
      .forEach((slotTime) => {
        // Parse slot time as UTC (API returns UTC)
        const slotDateTimeUtc = dayjs.utc(slotTime)
        
        // Format in clinic timezone using utility function
        const formattedTime = formatTimeSlotInClinicTimezone(
          slotDateTimeUtc,
          null,
          clinicLocation,
          eventDuration
        )
        
        // Get hour in clinic timezone for period categorization
        const slotDateTimeInTz = formattedTime.startInTz
        const hour = slotDateTimeInTz.hour()
        const dateInTz = slotDateTimeInTz.format('YYYY-MM-DD')

        const slotEnd = slotDateTimeUtc.add(eventDuration, 'minute')
        const isOverlapping = isSlotOverlapping(slotDateTimeUtc, slotEnd)

        const slotData = {
          start: slotDateTimeUtc, // Keep UTC for calculations
          end: slotEnd,
          label: formattedTime.label, // Formatted in clinic timezone
          labelWithTz: formattedTime.labelWithTz, // With timezone abbreviation
          id: slotDateTimeUtc.format('YYYY-MM-DD-HH-mm'),
          time: formattedTime.startFormatted, // Start time in clinic timezone
          clinicLocation, // Store clinic location for later use
          disabled: isOverlapping, // Mark as disabled if overlapping
          dateInTz, // Store the date in clinic timezone
        }

        // Categorize slots by time of day in clinic timezone
        // Only include slots for the selected date (next day slots are already filtered out)
        // Early Morning: 12 midnight to 7 AM (0-6) - only for selected date
        // Morning: 7 AM - 12 PM (7-11)
        // Afternoon: 12 PM - 5 PM (12-16)
        // Evening: 5 PM - 10 PM (17-21)
        // Night: 10 PM - 12 midnight (22-23)
        if (hour >= 0 && hour < 7) {
          // Early Morning: 12 midnight to 7 AM (only same day slots)
          periods.earlyMorning.slots.push(slotData)
        } else if (hour >= 7 && hour < 12) {
          periods.morning.slots.push(slotData)
        } else if (hour >= 12 && hour < 17) {
          periods.afternoon.slots.push(slotData)
        } else if (hour >= 17 && hour < 22) {
          periods.evening.slots.push(slotData)
        } else {
          // Night: 10 PM - 12 midnight (22-23)
          periods.night.slots.push(slotData)
        }
      })

    return periods
  }

  // Check if a slot overlaps with any booked slot
  const isSlotOverlapping = useMemo(() => {
    return (slotStart, slotEnd) => {
      if (!bookedSlots || bookedSlots.length === 0) {
        return false
      }

      // Convert slot times to dayjs for comparison
      const slotStartTime = dayjs.isDayjs(slotStart) ? slotStart : dayjs.utc(slotStart)
      const slotEndTime = dayjs.isDayjs(slotEnd) ? slotEnd : dayjs.utc(slotEnd)

      // Check overlap with each booked slot
      return bookedSlots.some((bookedSlot) => {
        if (!bookedSlot.startTime || !bookedSlot.endTime) {
          return false
        }

        const bookedStart = dayjs.utc(bookedSlot.startTime)
        const bookedEnd = dayjs.utc(bookedSlot.endTime)

        // Check if slots overlap
        // Two slots overlap if: slotStart < bookedEnd && slotEnd > bookedStart
        return slotStartTime.isBefore(bookedEnd) && slotEndTime.isAfter(bookedStart)
      })
    }
  }, [bookedSlots])


  // Memoize organized slots to prevent unnecessary re-renders that reset scroll
  const timePeriods = useMemo(() => organizeSlotsByTimePeriod(), [
    availableSlots,
    bookedSlots,
    clinicLocation,
    eventDuration,
    minDate,
    selectedDate,
  ])

  const handleSlotClick = (slot) => {
    // Don't allow clicking on disabled/overlapping slots
    if (slot.disabled) {
      return
    }
    onSlotSelect(slot)
  }

  // Filter slots based on showBlockedSlots toggle
  const filterSlots = (slots) => {
    if (showBlockedSlots) {
      return slots // Show all slots including blocked
    }
    return slots.filter(slot => !slot.disabled) // Hide blocked slots
  }

  // Count blocked slots for display
  const countBlockedSlots = useMemo(() => {
    let total = 0
    Object.keys(timePeriods).forEach(period => {
      total += timePeriods[period].slots.filter(slot => slot.disabled).length
    })
    return total
  }, [timePeriods])

  // Render time period section
  const renderTimePeriodSection = (period, slots) => {
    // Filter slots based on toggle
    const filteredSlots = filterSlots(slots)
    if (filteredSlots.length === 0) return null

    const isNight = period === 'night'
    const isEarlyMorning = period === 'earlyMorning'

    // For night and early morning slots, use Accordion for collapsible behavior
    if (isNight || isEarlyMorning) {
      const isExpanded = isNight ? nightSlotsExpanded : earlyMorningSlotsExpanded
      const setExpanded = isNight ? setNightSlotsExpanded : setEarlyMorningSlotsExpanded
      
      return (
        <Accordion
          key={period}
          expanded={isExpanded}
          onChange={(event, expanded) => setExpanded(expanded)}
          sx={{
            marginBottom: '30px',
            boxShadow: 'none',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: 'rgba(139, 92, 246, 0.05)',
              borderRadius: '8px 8px 0 0',
              minHeight: '48px',
              '&.Mui-expanded': {
                minHeight: '48px',
                borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
              },
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: 'rgb(151, 151, 151)',
                fontWeight: 'bold',
              }}
            >
              {timePeriods[period].label} ({filteredSlots.length} slots)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '16px' }}>
            <NextVisitSlotSelectorGridContainer>
              {filteredSlots.map((slot) => (
                <div key={slot.id}>
                  <NextVisitSlotSelectorButton
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                    disableElevation
                    disabled={slot.disabled || bookedSlotsLoading}
                    onClick={() => handleSlotClick(slot)}
                    sx={{
                      ...(slot.disabled && {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                        backgroundColor: '#e0e0e0 !important',
                        color: '#9e9e9e !important',
                        '&:hover': {
                          backgroundColor: '#e0e0e0 !important',
                        },
                      }),
                    }}
                  >
                    <div>
                      <div className="time-slot-text" style={{ fontSize: '1em' }}>
                        {slot.time}
                      </div>
                      {slot.disabled && (
                        <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>
                          Booked
                        </div>
                      )}
                    </div>
                  </NextVisitSlotSelectorButton>
                </div>
              ))}
            </NextVisitSlotSelectorGridContainer>
          </AccordionDetails>
        </Accordion>
      )
    }

    // For other periods, render normally
    return (
      <div key={period} style={{ marginBottom: '30px' }}>
        <Typography
          variant="subtitle2"
          style={{
            marginBottom: '15px',
            color: 'rgb(151, 151, 151)',
            fontWeight: 'bold',
            borderBottom: '2px solid #e0e0e0',
            paddingBottom: '8px',
          }}
        >
          {timePeriods[period].label} ({filteredSlots.length} slots)
        </Typography>

        <NextVisitSlotSelectorGridContainer>
          {filteredSlots.map((slot) => (
            <div key={slot.id}>
              <NextVisitSlotSelectorButton
                variant="contained"
                color="primary"
                size="small"
                fullWidth
                disableElevation
                disabled={slot.disabled || bookedSlotsLoading}
                onClick={() => handleSlotClick(slot)}
                sx={{
                  ...(slot.disabled && {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    backgroundColor: '#e0e0e0 !important',
                    color: '#9e9e9e !important',
                    '&:hover': {
                      backgroundColor: '#e0e0e0 !important',
                    },
                  }),
                }}
              >
                <div>
                  <div className="time-slot-text" style={{ fontSize: '1em' }}>
                    {slot.time}
                  </div>
                  {slot.disabled && (
                    <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>
                      Booked
                    </div>
                  )}
                </div>
              </NextVisitSlotSelectorButton>
            </div>
          ))}
        </NextVisitSlotSelectorGridContainer>
      </div>
    )
  }

  return (
    <>
      {/* Loading state */}
      {instructionsDiv}

      {/* Toggle to show/hide blocked slots */}
      {countBlockedSlots > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginBottom: '12px',
            padding: '8px 12px',
            backgroundColor: showBlockedSlots ? 'rgba(239, 68, 68, 0.08)' : 'rgba(139, 92, 246, 0.05)',
            borderRadius: '8px',
            border: `1px solid ${showBlockedSlots ? 'rgba(239, 68, 68, 0.2)' : 'rgba(139, 92, 246, 0.15)'}`,
            transition: 'all 0.2s ease',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showBlockedSlots}
                onChange={(e) => setShowBlockedSlots(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#ef4444',
                    '&:hover': {
                      backgroundColor: 'rgba(239, 68, 68, 0.08)',
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#ef4444',
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: '13px',
                  color: showBlockedSlots ? '#dc2626' : '#6b7280',
                  fontWeight: 500,
                }}
              >
                Show booked slots ({countBlockedSlots})
              </Typography>
            }
            labelPlacement="start"
            sx={{ marginRight: 0, gap: '8px' }}
          />
        </Box>
      )}

      {loading && (
        <div
          style={{
            textAlign: 'center',
            padding: '40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <CircularProgress color="primary" size={32} />
          <Typography variant="h6" style={{ color: '#666' }}>
            Loading available slots...
          </Typography>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div
          style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: '#ffebee',
            borderRadius: '8px',
            border: '1px solid #ef5350',
            margin: '20px 0',
          }}
        >
          <Typography variant="h6" style={{ color: '#c62828', marginBottom: '10px' }}>
            ⚠️ Error Loading Slots
          </Typography>
          <Typography style={{ color: '#666' }}>{error}</Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={fetchAvailableSlots}
            style={{ marginTop: '15px' }}
          >
            Try Again
          </Button>
        </div>
      )}

      {/* No slots available */}
      {!loading && !error && availableSlots.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6" style={{ color: '#666', marginBottom: '10px' }}>
            📅 No Available Slots
          </Typography>
          <Typography style={{ color: '#999' }}>
            No available slots found for {selectedDate?.format('dddd, MMMM D, YYYY')}
          </Typography>
        </div>
      )}

      {/* Available slots organized by time periods */}
      {!loading && !error && availableSlots.length > 0 && (
        <div className="mt-4">
          {/* Render each time period */}
          {Object.keys(timePeriods).map((period) => (
            <React.Fragment key={period}>
              {renderTimePeriodSection(period, timePeriods[period].slots)}
            </React.Fragment>
          ))}

          {/* Summary */}
          <div
            style={{
              marginTop: '30px',
              padding: '15px',
              backgroundColor: '#e8f5e8',
              borderRadius: '8px',
              border: '1px solid rgb(173, 240, 176)',
              textAlign: 'center',
            }}
          >
            <Typography style={{ color: '#4caf51', fontWeight: 'bold' }}>
              📊 Total Available Slots: {availableSlots.length - countBlockedSlots}
              {countBlockedSlots > 0 && !showBlockedSlots && (
                <span style={{ color: '#9e9e9e', fontWeight: 'normal', marginLeft: '8px' }}>
                  ({countBlockedSlots} booked slots hidden)
                </span>
              )}
            </Typography>
          </div>
        </div>
      )}
    </>
  )
}
